import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import {
  AuditLog,
  AuditLogType,
  AuditLogTypeLabels,
} from "../../models/AuditLog";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { dialogMedium } from "@/lib/dialogSize";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";

const ViewMarkNotFoundFileForRetryAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => {
  const generateDynamicLink = (entityId: string | null, type: AuditLogType) => {
    if (entityId === null) return `/organization/${organizationId}/audit-logs`;

    switch (type) {
      case AuditLogType.MarkNotFoundCaseFileForRetry:
        return `/organization/${organizationId}/case/${entityId}`;
      case AuditLogType.MarkNotFoundIntakeFileForRetry:
        return `/organization/${organizationId}/intake/${entityId}`;
      case AuditLogType.MarkNotFoundFirmDocumentForRetry:
        return `/organization/${organizationId}/firm-documents-folder/${entityId}`;
      case AuditLogType.MarkNotFoundProviderFileForRetry:
        return `/organization/${organizationId}/provider/${entityId}`;
      case AuditLogType.MarkNotFoundTemplateFileForRetry:
        return `/organization/${organizationId}/template/${entityId}`;
      default:
        return `/organization/${organizationId}/audit-logs`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${dialogMedium} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {AuditLogTypeLabels[auditLog.eventType]}
              </DialogTitle>
            </DialogHeader>

            <Separator />
            {auditLog.additionalData ? (
              (() => {
                try {
                  const additionalDataAsJson = JSON.parse(
                    auditLog.additionalData
                  );
                  return (
                    <>
                      <span className="text-sm font-bold">
                        Reason:
                      </span>
                      <span
                        className="text-sm whitespace-pre-wrap break-words overflow-y-auto max-h-[30vh] pr-2"
                      >
                        {additionalDataAsJson.Reason}
                      </span>
                    </>
                  );
                } catch (error) {
                  return (
                    <></>
                  );
                }
              })()
            ) : (
              <></>
            )}

            {auditLog.additionalData && (
              <Separator />
            )}

            {auditLog.additionalData ? (
              (() => {
                try {
                  const additionalDataAsJson = JSON.parse(
                    auditLog.additionalData
                  );
                  // An All Files entry names the migration types it retried and what was Not Found when it was
                  // queued; it has no single entity or file. Older entries name a client configuration instead.
                  if (!additionalDataAsJson.MigrationEntityId) {
                    return (
                      <>
                        {additionalDataAsJson.ClientConfigurationName && (
                          <LabelValuePair
                            label="Client Configuration"
                            value={additionalDataAsJson.ClientConfigurationName}
                          />
                        )}
                        {Array.isArray(additionalDataAsJson.MigrationTypes) && (
                          <LabelValuePair
                            label="Migration Types"
                            value={
                              additionalDataAsJson.MigrationTypes.length > 0
                                ? additionalDataAsJson.MigrationTypes.map(
                                    (t: string) =>
                                      MigrationTypeLabels[(t.charAt(0).toLowerCase() + t.slice(1)) as MigrationType] ?? t
                                  ).join(", ")
                                : "None switched on"
                            }
                          />
                        )}
                        {typeof additionalDataAsJson.FileCount === "number" && (
                          <LabelValuePair
                            label="Queued"
                            value={`${additionalDataAsJson.FileCount.toLocaleString()} files across ${(
                              additionalDataAsJson.EntityCount ?? 0
                            ).toLocaleString()} entities`}
                          />
                        )}
                      </>
                    );
                  }
                  return (
                    <>
                      <div className="flex flex-row gap-1">
                        <span className="text-sm font-bold">
                          Migration Entity ID:
                        </span>
                        <span className="text-sm">
                          <a
                            href={generateDynamicLink(
                              additionalDataAsJson.MigrationEntityId,
                              auditLog.eventType
                            )}
                            className="hover:underline text-primary"
                          >
                            {additionalDataAsJson.MigrationEntityId}
                          </a>
                        </span>
                      </div>
                      <div className="flex flex-row gap-1">
                        <span className="text-sm font-bold">
                          File ID:
                        </span>
                        <span className="text-sm">
                          {additionalDataAsJson.FileId}
                        </span>
                      </div>
                    </>
                  );
                } catch (error) {
                  return (
                    <span className="text-sm">
                      Failed to parse additionalData
                    </span>
                  );
                }
              })()
            ) : (
              <></>
            )}

            <LabelValuePair
              label="Performed By"
              value={auditLog.performerEmailAddress}
            />

            <LabelValuePair label="Event Date" value={auditLog.eventDate} />
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewMarkNotFoundFileForRetryAuditLogDetailsModal;
